'use client'; // Redundant for now but just as a hint for the future

import { DataLayerBridge } from '@client/bridges/DataLayer';
import updateSearchData from '@client/bridges/SearchData';
import { LiraRunAdvScriptWrapperForListing } from '@client/components/Adv/Lira/LiraRunAdvScriptWrapper/LiraRunAdvScriptWrapperForListing';
import { useLiraLazyInterstitialForDesktop } from '@client/components/Adv/Lira/useLiraLazyInterstitialForDesktop';
import { ListingLinks } from '@client/components/SEO/InternalLinks/types';
import ListingContainer from '@client/components/Search/Containers/ListingContainer';
import { initPresencePing } from '@client/utilities/presence-ping';
import { updateItems } from '@reducers/items';
import { Provider as ReduxProvider } from 'react-redux';
import {
  startUpdating,
  stopUpdating,
  updateAdType,
  updateCategory,
  updateOrder,
  updateQSO,
  updateQuery,
  updateShippableOnly,
  updateUrgent,
} from '@reducers/search';
import { getStore, type RootState } from '@reducers/index';
import { AdvScripts, LiveRampScript } from '@sbt-web/adv';
import { getPublicUser } from '@sbt-web/auth';
import { initDidomi } from '@sbt-web/didomi';
import { CategoryID, HTTPStatusCode, type Faq } from '@sbt-web/network/types';
import { type SeoMetaPayload } from '@sbt-web/networking';
import { applyContextToFunc } from '@sbt-web/request-id-handler';
import { defaultSocialMetaValues, socialMeta } from '@sbt-web/head-properties';
import {
  dispatchEventForClients,
  isBrowser,
  onDOMContentLoaded,
  queueTask,
} from '@sbt-web/utils';
import { Category, getCategoryByFriendly } from '@sbt-web/category-utils';
import { LISTING_PAGE_LIMIT } from '@shared/constants';
import { Theme, getCategoryThemeById } from '@shared/helpers/Themes';
import {
  fetchFaqResponseHandler,
  geogosResponseHandler,
  getListingLinksResponseHandler,
  getSeoDataResponseHandler,
  setEnviromentId,
  updatePageNumber,
} from '@shared/listinginitialProps';
import type { ListingQuery } from '@shared/routes';
import listingSchema from '@shared/tools/seo/listing';
import { hasFatalErrors, hasTimeoutErrors } from '@tools/errorHelpers';
import { getFiltersStore } from '@tools/search/getFiltersStore';
import { ListingHelper } from '@tools/search/ListingHelper';
import { ImgObjSchemaListing } from '@tools/seo/ImgObjSchema/Listing';
import { mapListingQueryToSeoRequest } from '@tools/seo/utils';
import { sendEvent as trackListingPageView } from '@tools/tracking/AdSearchTracking';
import { getOrCreatePulse } from '@tools/tracking/utils';
import type { Request, Response } from 'express';
import type { NextPageContext } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import React, { useEffect, useMemo } from 'react';
import type { ThunkDispatch } from '@reduxjs/toolkit';
import { setStatusOnServer, type BasePageProps } from './_app';
import {
  updateFiltersConfigRequest,
  updateFiltersConfigSuccess,
} from '@reducers/filtersConfig';

export interface ListingContext extends NextPageContext {
  query: ListingQuery;
  req: Request;
  res?: Response;
}

interface ListingProps {
  seo?: SeoMetaPayload;
  internalLinks: ListingLinks | null;
  faqData: Faq | null;
  category?: Category;
  initialState: RootState;
}

export type ListingPageProps = ListingProps & BasePageProps;

const initModuleManager = () => {
  if (window.subito.dataLayer?.search && window.subito.dataLayer?.ads) {
    void trackListingPageView();
  }

  window.subito.dataLayer?.subscribe('ads', () => {
    updateSearchData();
    dispatchEventForClients('subito:listing:reloaded', null, ['gtm']);
    trackListingPageView();
  });
};

export default function Listing(props: ListingPageProps) {
  const handleItemClick = useLiraLazyInterstitialForDesktop();

  useEffect(() => {
    initDidomi(getOrCreatePulse(), 'listing');

    onDOMContentLoaded(initModuleManager);

    new DataLayerBridge('listing').register(getPublicUser());
    updateSearchData();

    queueTask(() => {
      initPresencePing(true);
    }, 'user-visible');
  }, []);

  const {
    seo,
    faqData,
    internalLinks,
    initialState: { items, search },
  } = props;

  const adItems = useMemo(() => items?.list.map((i) => i.item) ?? [], [items]);

  return (
    <>
      <Head>
        <title key="page-title">{seo?.title ?? 'Subito.it'}</title>

        {seo !== undefined ? (
          <meta
            name="description"
            key="description"
            content={seo.description}
          />
        ) : null}

        <meta
          name="robots"
          key="robots"
          content={
            items?.total === 0 || seo === undefined
              ? 'noindex,nofollow'
              : seo.metaRobots
          }
        />

        {seo !== undefined ? (
          <link rel="canonical" key="canonical-url" href={seo.canonicalUrl} />
        ) : null}

        {seo !== undefined ? (
          <script
            key="listing-schema"
            id="listing-schema"
            {...listingSchema({
              items: adItems,
              description: seo.description,
              category: search.category,
              searchQuery: search.query,
              brandName:
                search.filtersLabels['cb'] || search.filtersLabels['bb'],
              modelName: search.filtersLabels['cm'],
              page: search.page,
              itemsCount: items?.total ?? 0,
            })}
          />
        ) : null}

        <ImgObjSchemaListing adItems={adItems} />

        {socialMeta({
          ...defaultSocialMetaValues,
          description: seo?.description,
        })}

        {/* "automotive-ads" tag */}
        {search.category.id === CategoryID.Auto && (
          <meta
            name="google-site-verification"
            content="dz8bU3soSfObHF0ziAEj5Y4QZ0gVKqsxdOB95KHV7Q0"
          />
        )}
      </Head>

      <AdvScripts
        Script={Script}
        config={{
          includeOw: false,
          includeTam: false,
          includeAdSense: true,
          includePrebid: true,
          publicPath: '/static/script',
        }}
      />
      <LiveRampScript Script={Script} />
      <LiraRunAdvScriptWrapperForListing
        category={search.category}
        query={search.query}
        pageIndex={search.page}
        adType={search.adType}
        geo={search.geo}
        filtersLabels={search.filtersLabels}
        radiusSearch={search.radiusSearch ?? undefined}
        includeShippableOnly={search.includeShippableOnly}
        isModalOpened={search.filtersDialogOpen ?? search.radiusDialogOpen}
        adItems={adItems}
      />

      <ReduxProvider store={getStore(props.initialState, !isBrowser())}>
        <ListingContainer
          seo={seo}
          internalLinks={internalLinks}
          faqData={faqData}
          handleItemClick={handleItemClick}
        />
      </ReduxProvider>

      <Script
        strategy="afterInteractive"
        id="appsflyer-script"
        src="/static/script/appsflyer.js"
      />
    </>
  );
}

const getInitialProps = async ({
  req,
  query,
  res,
}: ListingContext): Promise<ListingPageProps> => {
  const store = getStore(undefined, __SERVER__);

  const Helper = new ListingHelper(store, query);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: ThunkDispatch<RootState, void, any> = store.dispatch;
  const category = getCategoryByFriendly(query.category);
  const adType = Helper.getAdTypeForCategory(category);

  // When requesting a page over the limit, a 404 should be thrown
  // We should not allow the user to request a page over the limit,
  // this causes errors in the backend.
  // If the adType is null a 404 should be thrown
  if (Number.parseInt(query.o as string) > LISTING_PAGE_LIMIT || !adType) {
    return {
      ...setStatusOnServer(HTTPStatusCode.NotFound, res),
      pageName: 'listing',
      theme: Theme.Red,
      internalLinks: null,
      faqData: null,
      initialState: store.getState(),
    };
  }

  const { search } = store.getState();

  const environmentId = setEnviromentId(req, store, dispatch);

  // Here we need to check if we really have to dispatch something
  // Because, even if this code is executed, it doesn't mean that everything
  // in the state should be changed according to the URL
  // This is not done in every case because some of the Actions do a check.
  dispatch(startUpdating());

  dispatch(updateCategory(category));
  dispatch(updateAdType(adType));
  dispatch(updateQuery(query.q));

  dispatch(updateFiltersConfigRequest());
  const currentConfig = await getFiltersStore(category.id, adType);
  dispatch(updateFiltersConfigSuccess(currentConfig));

  Helper.checkThenDispatch(
    search.qso !== !!query.qso,
    updateQSO(query.qso === 'true')
  );
  Helper.checkThenDispatch(
    search.includeShippableOnly !== !!query.shp,
    updateShippableOnly(query.shp === 'true')
  );
  Helper.checkThenDispatch(
    search.includeUrgent !== !!query.urg,
    updateUrgent(query.urg === 'true')
  );

  dispatch(updateOrder(query.order ?? category.orders[0]));

  if (currentConfig) {
    currentConfig.sparseFilters.forEach((filter) =>
      Helper.checkDynamicFilterThenDispatch(filter, search)
    );
  }

  const isLocationOk = await geogosResponseHandler(Helper, search, res);
  if (!isLocationOk) {
    return {
      ...setStatusOnServer(HTTPStatusCode.NotFound, res),
      pageName: 'listing',
      faqData: null,
      theme: Theme.Red,
      internalLinks: null,
      initialState: store.getState(),
    };
  }

  // Updating the page should always be kept at the end of this process
  // Because otherwise dispatching any other search param update will
  // reset its value to 1
  updatePageNumber(query, Helper, search);

  dispatch(stopUpdating());

  // Get updated searchState that will be mapped to SeoRequest.
  // TODO: investigate possible implications. The await does not actually do anything
  await dispatch(updateItems(res));
  const { search: searchState, filtersConfig: filtersConfigState } =
    store.getState();

  const metiSeoRequest = mapListingQueryToSeoRequest(query, searchState);

  const metiRequest: Promise<SeoMetaPayload> = getSeoDataResponseHandler(
    searchState,
    filtersConfigState,
    metiSeoRequest,
    res
  );

  const internalLinksRequest = getListingLinksResponseHandler(
    metiRequest,
    searchState,
    res,
    environmentId
  );

  const pitoSeoRequest = mapListingQueryToSeoRequest(
    query,
    searchState,
    'values'
  );
  const pitoRequest = fetchFaqResponseHandler(pitoSeoRequest, req, res);

  const [seoData, internalLinks, faqData]: [
    SeoMetaPayload,
    ListingLinks | null,
    Faq | null,
  ] = await Promise.all([metiRequest, internalLinksRequest, pitoRequest]);

  if (hasFatalErrors(res)) {
    return {
      ...setStatusOnServer(
        hasTimeoutErrors(res)
          ? HTTPStatusCode.GatewayTimeout
          : HTTPStatusCode.BadRequest,
        res
      ),
      pageName: 'listing',
      theme: Theme.Red,
      internalLinks: null,
      faqData: null,
      initialState: store.getState(),
    };
  }

  const initialState = store.getState();

  return {
    ...setStatusOnServer(HTTPStatusCode.OK, res),
    pageName: 'listing',
    theme: getCategoryThemeById(category.id),
    seo: seoData,
    internalLinks,
    faqData,
    category,
    initialState,
  };
};

Listing.getInitialProps = applyContextToFunc(getInitialProps);
